/**
 * Combobox de selección de medio con filtros por kind y coverage.
 * Implementado con Select de Radix (sin dep cmdk).
 */
import { useQuery } from "@tanstack/react-query";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useOutletKinds } from "@/hooks/use-outlet-kinds";
import { media, type OutletKind, type Coverage } from "@/lib/api";

export interface OutletPickerProps {
  value: number | null;
  onChange: (outletId: number, accepts_formats: string[]) => void;
  kindFilter?: OutletKind;
  coverageFilter?: Coverage;
  disabled?: boolean;
  label?: string;
}

export function OutletPicker({
  value, onChange, kindFilter, coverageFilter, disabled, label = "Medio",
}: OutletPickerProps) {
  const { labels: kindLabels } = useOutletKinds();
  const { data, isLoading } = useQuery({
    queryKey: ["media", "outlets", kindFilter, coverageFilter],
    queryFn: () => media.listOutlets({
      kind: kindFilter,
      coverage: coverageFilter,
      is_active: true,
      limit: 500,
    }),
  });

  if (isLoading) return <Skeleton className="h-10 w-full" />;

  // Agrupar por kind para mostrar separadores visuales
  const groups: Record<string, typeof data> = {};
  for (const o of data ?? []) {
    (groups[o.kind] ||= []).push(o);
  }

  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select
        disabled={disabled}
        value={value ? String(value) : ""}
        onValueChange={(v) => {
          const id = Number(v);
          const outlet = (data ?? []).find(o => o.id === id);
          if (outlet) onChange(id, outlet.accepts_formats);
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Seleccionar medio..." />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(groups).map(([kind, list]) => (
            <div key={kind}>
              <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-ink-400 font-semibold">
                {kindLabels[kind] ?? kind}
              </div>
              {(list ?? []).map(o => (
                <SelectItem key={o.id} value={String(o.id)}>
                  <div className="flex items-center justify-between w-full gap-3">
                    <span>{o.name}</span>
                    {o.coverage && (
                      <span className="text-[10px] uppercase text-ink-400 ml-2">
                        {o.coverage}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </div>
          ))}
          {!(data ?? []).length && (
            <div className="px-2 py-3 text-xs text-ink-400 text-center">
              No hay medios. Crea uno en /media/outlets.
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
